import * as React from 'react';
import {
    IObservableValue,
    action,
    observable,
    computed,
    makeObservable,
} from 'mobx';
import i18next, { i18n } from 'i18next';
import 'pepjs'; // pointerEvent polyfill

import MessageManager from './managers/MessageManager';
import { BackButtonManager, enableBackButtonManagerDebugFlag } from './managers/BackButtonManager';
import { EventManager } from './managers/EventManager';
import { ExportManager } from './managers/ExportManager';
import { ImageScaleManager } from './managers/ImageScaleManager';
import { IntroManager } from './managers/IntroManager';
import { SessionExpiredError } from './custom-errors/SessionExpiredError';
import { SessionManager } from './managers/SessionManager';

import { AsyncFunction } from '@egr/xbox/utils/Types';
import { DialogManager } from '@egr/xbox/egr-gui-dialogs/DialogManager';
import { Logger } from '@egr/xbox/utils/errors/Logger';
import { OBK_IMAGE_SIZE } from '@egr/xbox/basket-view/Utils/Constants';
import { SceneManager } from '@egr/xbox/threeD/managers/SceneManager';
import { UpdateJourneyManager } from '@egr/xbox/base-app/managers/UpdateJourneyManager';
import { assert, logPropertyAccess, wrapDebugTimer } from '@egr/xbox/utils/Debug';
import { captureException, ErrorOptions, errorTrackingEnabled } from '@egr/xbox/utils/Error';
import { clearDataErrorBreadcrumbs } from '@egr/xbox/utils/errors/DataErrors';
import { convertLanguageToLocale } from '@egr/xbox/utils/String';
import { deviceHasMouse, ios } from '@egr/xbox/utils/UserAgent';
import { getEnvBoolean, getEnvArray, getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { getPromiseCallbacks, PromiseCallbacks } from '@egr/xbox/utils/promise/Callbacks';
import { registerAjaxErrorHandler, AjaxSoapErrorInfo, retryNetworkRequests } from '@egr/xbox/utils/AjaxErrorHandler';

import { Application } from '@egr/wcf/modules/core';
import { Basket } from '@egr/wcf/modules/bsk';
import { CustomError } from '@egr/wcf/modules/utils/error';
import { EaiwsSession, SoapError } from '@egr/wcf/modules/eaiws';
import { ListenableEvent } from '@egr/wcf/modules/utils/ListenableEvent';
import { LogData } from '@egr/wcf/modules/eaiws/session';
import { isNotNullOrEmpty, isNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { wcfConfig } from '@egr/wcf/modules/utils';

export enum EventReason {
    /**
     * For some language the default encoding must be changed. Right now, a new
     * EAIWS session is the only way to ensure the correct encoding.
     */
    DEFAULT_ENCODING_CHANGED = 1 << 0,
    PCON_LOGIN_STATE_CHANGED = 1 << 1,
}

export interface SessionTaskQueue<Arguments extends Array<unknown> = []> {
    addTask: (debugLabel: string, callback: AsyncFunction<void, Arguments>) => void;
}

interface PreInitializationTasks {
    postConnect: SessionTaskQueue;
}

/**
 * Note: it is not guaranteed that this event will ever be triggered
 */
export interface PreInitializationEvent {
    type: 'pre-initialization';
    tasks: PreInitializationTasks;
    primaryDataLanguage?: string;
}

export interface OpenSessionEventOptions {
    primaryDataLanguage?: string;
    newProject?: boolean;
    importFormat?: string;
    eventReason?: EventReason;
}

export interface OpenSessionTasks {
    /**
     * @deprecated Note: newer EAIWS versions support the clearing of an already
     * established session. Therefore, in most cases, the session will already
     * have been created before this event is triggered.
     */
    preConnect: SessionTaskQueue<[OpenSessionEventOptions]>;
    /**
     * In this step the session data should be loaded (e.g. OBK import)
     */
    loading: SessionTaskQueue<[OpenSessionEventOptions]>;
    /**
     * This step can be used to load settings from the session itself.
     * (e.g. data stored in the OBK file)
     */
    postLoading: SessionTaskQueue<[OpenSessionEventOptions]>;
    preProcessing: SessionTaskQueue<[OpenSessionEventOptions]>;
    processing: SessionTaskQueue<[OpenSessionEventOptions]>;
    postProcessing: SessionTaskQueue<[OpenSessionEventOptions]>;
}

export interface OpenSessionEvent {
    type: 'open';
    tasks: OpenSessionTasks;
    options: OpenSessionEventOptions;
}

export interface CloseSessionTasks {
    preCleaning: SessionTaskQueue<[CloseSessionEventOptions]>;
    cleaning: SessionTaskQueue<[CloseSessionEventOptions]>;
    postClose: SessionTaskQueue<[CloseSessionEventOptions]>;
}

export interface CloseSessionEventOptions {
    eventReason?: EventReason;
}

export interface CloseSessionEvent {
    type: 'close';
    tasks: CloseSessionTasks;
    options: CloseSessionEventOptions;
}

export interface SaveSessionTasks {
    updating: SessionTaskQueue;
    saving: SessionTaskQueue;
}

export interface SaveSessionEvent {
    type: 'save';
    tasks: SaveSessionTasks;
}

export type SessionEvent = OpenSessionEvent | SaveSessionEvent | CloseSessionEvent | PreInitializationEvent;

export interface AppProps {
}

class SessionListableEvent extends ListenableEvent<SessionEvent, App> {
    public readonly preInitialization: ListenableEvent<PreInitializationEvent, App>;
    public readonly open: ListenableEvent<OpenSessionEvent, App>;
    public readonly close: ListenableEvent<CloseSessionEvent, App>;
    public readonly save: ListenableEvent<SaveSessionEvent, App>;

    public constructor(app: App) {
        super(app);

        this.preInitialization = new ListenableEvent(app);
        this.open = new ListenableEvent(app);
        this.close = new ListenableEvent(app);
        this.save = new ListenableEvent(app);
    }

    /**
     * @deprecated Please use one of the more specify properties to attach new event handler. (e.g.: `.open.addListener(...)`)
     */
    public override addListener(listener: (param: SessionEvent, owner: App) => void): void {
        // Note: this override is only used to attach the @deprecated comment
        super.addListener(listener);
    }

    public override trigger(event: SessionEvent): void {
        super.trigger(event);

        switch (event.type) {
            case 'pre-initialization':
                this.preInitialization.trigger(event);
                break;
            case 'open':
                this.open.trigger(event);
                break;
            case 'save':
                this.save.trigger(event);
                break;
            case 'close':
                this.close.trigger(event);
                break;
            default:
                Logger.error(new Error('unhandled event'), event);
                break;
        }
    }
}

interface AppEvents {
    // Note: do not use async events. The handlers should use the `Lock` instances provided
    // via the `stages` argument for async tasks
    session: SessionListableEvent;
    project: {
        changed: EventManager<{
            // If set to `true`, the project must be saved immediately.
            // Note: this may lead to a situation where user interaction is required (e.g. project was not saved before)
            saveImmediately?: boolean;
            // If set to `true`, the event should be ignored if the project was already saved before
            ignoreIfSavedBefore?: boolean;
        }>;
    };
    viewCreated: ListenableEvent<void, App>;
}

export type DisplayEvent = 'fullscreenOn' | 'fullscreenOff' | 'rotateLock' | 'rotateUnlock' | 'rotateRestore';

export type StatusbarEvent =
    'statusbarShow' | 'statusbarHide' |
    'statusbarFontDark' | 'statusbarFontLight' |
    'saveState' | 'restoreState';

type ModalEvent = 'modalDialogOpen' | 'modalDialogClose';

export interface MobileState {
    statusbarColor?: string;
    font?: 'dark' | 'light';
    showStatusbar?: boolean;
}

export interface MobileEvents {
    state: MobileState;
    display: ListenableEvent<DisplayEvent, App>;
    statusbar: ListenableEvent<StatusbarEvent, App>;
    statusbarColor: ListenableEvent<string, App>;
    modal: ListenableEvent<ModalEvent, App>;
}

type NamedActionCallback = (args: unknown) => Promise<unknown>;

interface NamedAction {
    name: string;
    argument: unknown;
}

export class CancelNamedActions extends CustomError {
    public constructor(message?: string) {
        super(message);

        Object.setPrototypeOf(this, CancelNamedActions.prototype);
    }
}

const getAppPromiseCallbacks: PromiseCallbacks<App> = getPromiseCallbacks<App>();

export function getApp<T extends App>(): Promise<T> {
    return getAppPromiseCallbacks.promise as unknown as Promise<T>;
}

export type App = BaseApp<AppProps>;

export interface DefaultBaseAppProps {
    app: App;
}

export abstract class BaseApp<TProps extends AppProps = AppProps> extends React.Component<TProps, {}> {
    public readonly version: string = getEnvVar('BUILD_COMMIT', 'develop');

    public readonly viewerID: string = 'scene-dummy';

    public readonly mobileEvents: MobileEvents = {
        state: {},
        display: new ListenableEvent<DisplayEvent, App>(this),
        statusbar: new ListenableEvent<StatusbarEvent, App>(this),
        modal: new ListenableEvent<ModalEvent, App>(this),
        statusbarColor: new ListenableEvent<string, App>(this)
    };

    public readonly events: AppEvents = {
        session: new SessionListableEvent(this),
        project: {
            changed: new EventManager()
        },
        viewCreated: new ListenableEvent<void, App>(this)
    };

    // Important: must be initialized by the child class
    public readonly sessionManager: SessionManager;
    public i18nextInstance: i18n;
    public readonly introManager: IntroManager | undefined;
    public UpdateJourneyManager: UpdateJourneyManager | undefined;
    public readonly messageManager: MessageManager = new MessageManager();
    public readonly backButtonManager: BackButtonManager = new BackButtonManager();
    public readonly sceneManager: SceneManager;
    public readonly dialogManager: DialogManager = new DialogManager();

    // will be initialized in this class
    public readonly coreApp: Application;
    public readonly eaiwsSession: EaiwsSession;
    public readonly eaiwsBasket: Basket;
    public readonly exportManager: ExportManager = new ExportManager(this);
    public readonly imageScaleManager: ImageScaleManager = new ImageScaleManager();

    // should be resolved after the app was fully initialized
    public readonly initialization: Promise<void>;
    protected readonly resolveInitialization: (data: void) => void;

    /**
     * This is a MobX-Observable, so do not forget observer decorator when you use this in a component
     */
    @computed
    public get language(): string {
        return this.i18nextLanguage.get();
    }

    protected i18nextLanguage: IObservableValue<string>;

    private namedActions: Map<string, NamedActionCallback> = new Map<string, NamedActionCallback>();

    constructor(props: TProps) {
        super(props);

        makeObservable(this);

        const initializationPromise: PromiseCallbacks = getPromiseCallbacks();
        this.initialization = initializationPromise.promise;
        // Must be called after the initialization is finished
        this.resolveInitialization = initializationPromise.resolve;

        this.i18nextLanguage = observable.box('en');
        assert(i18next.isInitialized, 'i18next instance must be initialized');
        this.i18nextInstance = i18next;
        this.updateLanguage();
        this.i18nextInstance.on('languageChanged', this.updateLanguage.bind(this));

        // register error handlers
        registerAjaxErrorHandler({
            networkError: this.handleNetworkError.bind(this, retryNetworkRequests),
            soapError: (error: AjaxSoapErrorInfo): void => {
                error.reject();
            },
            eaiwsSessionExpired: (error: AjaxSoapErrorInfo): void => {
                error.reject(new SessionExpiredError(error.responseError));
            }
        });

        const ErrorIgnoreList = getEnvArray('DEBUG_IGNORE_ERRORS', '|');
        // FixMe:
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        window.addEventListener('unhandledrejection', async (event: PromiseRejectionEvent): Promise<void> => {
            const reason: Error = (
                event.reason instanceof Error ?
                event.reason : (
                    event.reason != null ?
                    new Error(event.reason) : new Error('unhandledrejection without .reason')
                )
            );

            if (ErrorIgnoreList.includes(reason.message)) {
                console.error('Ignored error', reason);
                return;
            }

            if (errorTrackingEnabled()) {
                let extraOptions: ErrorOptions | undefined;

                if (reason instanceof SoapError) {
                    try {
                        const log: LogData | undefined = await this.eaiwsSession.session.getSessionLog(true);
                        if (log != null) {
                            extraOptions = {
                                extra: {
                                    eaiwsLog: log.data
                                }
                            };
                        }
                    } catch {
                        // do nothing
                    }

                }

                captureException(reason, 'fatal', extraOptions);
            }

            window.setTimeout((): void => {
                this.handleUnhandledRejection(reason);
            });
        });

        getAppPromiseCallbacks.resolve(this);

        if (getEnvBoolean('DEBUG_APP_TRAP', false)) {
            return logPropertyAccess('App', this);
        }
    }

    public override shouldComponentUpdate(nextProps: AppProps, nextState: {}): boolean {
        // Ensure that this component is never updated in order to keep the
        // canvas 3d context alive.
        // (Which should not happen under normal conditions anyway, since
        // this component has no state and no props.)
        return false;
    }

    public override componentDidMount(): void {
        this.initializeCore();

        if (getEnvBoolean('DEBUG_BACK_BUTTON_MANAGER')) {
            enableBackButtonManagerDebugFlag(this.backButtonManager);
        }
    }

    public registerNamedAction(name: string, callback: NamedActionCallback): void {
        if (this.namedActions.has(name)) {
            throw new Error('Name already taken: ' + name);
        }

        this.namedActions.set(name, callback);
    }

    public async triggerNamedActions(namedActions: Array<NamedAction>): Promise<void> {
        namedActions.forEach((namedAction: NamedAction): void => {
            if (!this.namedActions.has(namedAction.name)) {
                throw new Error('Unknown action: ' + namedAction.name);
            }
        });

        for (const namedAction of namedActions) {
            try {
                await this.namedActions.get(namedAction.name)!(namedAction.argument);
            } catch (error) {
                if (error instanceof CancelNamedActions) {
                    break;
                }
                throw error;
            }
        }
    }

    public abstract handleNetworkError(retryCallback: VoidFunction): void;
    protected abstract handleUnhandledRejection(error: Error): void;

    protected initializeCore(): void {
        wcfConfig.articleConfigurationOptions = {
            highResPropValueIcons: window.devicePixelRatio > 1.5,
            fetchPropValueImages: true
        };
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        (this.coreApp as Application) = new Application();

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        (this.eaiwsSession as EaiwsSession) = new EaiwsSession();

        if (getEnvBoolean('DEBUG_TRACE_OBK_UPLOAD', false)) {
            this.eaiwsSession.uploadFileToUrl = wrapDebugTimer('upload file to url', this.eaiwsSession.uploadFileToUrl.bind(this.eaiwsSession));
        }

        this.initializeCoreBasket();

        // enable w-cf hover effects
        if (document.documentElement != null && (!ios || deviceHasMouse())) {
            document.documentElement.classList.add('wcf-hover-enabled');
        }

        this.events.session.close.addListener((event: CloseSessionEvent): void => {
            event.tasks.cleaning.addTask('clear core document', async (): Promise<void> => {
                if (this.sceneManager.articleManagerCreated) {
                    this.coreApp.document.clear();
                }
            });
            event.tasks.cleaning.addTask('clearDataErrorBreadcrumbs', async () => clearDataErrorBreadcrumbs());
        });

        if (getEnvBoolean('EAIWS_SET_LOCALE', true)) {
            this.events.session.open.addListener((event: OpenSessionEvent): void => {
                event.tasks.preProcessing.addTask('setLocale', async (): Promise<void> => {
                    await this.eaiwsSession.session.setLocale(convertLanguageToLocale(this.language));
                });
            });
        }
    }

    protected initializeCoreBasket(): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        (this.eaiwsBasket as Basket) = new Basket(this.eaiwsSession);
        this.eaiwsBasket.generatedImageResolution = OBK_IMAGE_SIZE;
        this.eaiwsBasket.includeCalculationErrorsInInconsistencies = false;
    }

    @action
    protected updateLanguage(): void {
        if (isNotNullOrEmpty(this.i18nextInstance.languages[0])) {
            this.i18nextLanguage.set(this.i18nextInstance.languages[0]);
        } else if (isNullOrEmpty(this.language)) {
            this.i18nextLanguage.set('en');
        }
    }
}

export default App;